import { formatBytes } from './storage';

export interface DownloadProgress {
  /** Bytes received so far. Only ever increases, which is why it leads the UI. */
  loadedBytes: number;
  /** Bytes known to be needed — grows as files announce themselves. */
  totalBytes: number;
  /** 0–100 of `totalBytes`. Read the note below before trusting it. */
  percent: number;
  /** How many distinct files have reported progress. */
  files: number;
}

/**
 * Accumulate per-file download progress into one figure.
 *
 * transformers.js reports progress per file, restarting at zero for each one.
 * A model is many files, so a UI that forwards that number raw reads 70%, then
 * 34%, then 90% — three files, each honest, and together worse than no number
 * at all, because it looks like the download is losing ground.
 *
 * Bytes lead the display, because bytes only ever go up. The percentage is
 * shown alongside but is the weaker number: `totalBytes` counts only files that
 * have announced themselves, so it grows during the download and the percentage
 * can legitimately fall. That is what not knowing the manifest in advance
 * actually feels like. The fix is to lead with the number that cannot mislead,
 * not to smooth over the one that can.
 */
export function createDownloadProgress() {
  const seen = new Map<string, { loaded: number; total: number }>();

  return function update(p: { file: string; loaded: number; total: number }): DownloadProgress {
    // A zero total means the file has not reported a size yet. Recording it
    // would add a file to the count that contributes nothing to either sum.
    if (p.total > 0) {
      seen.set(p.file, { loaded: Math.min(p.loaded, p.total), total: p.total });
    }

    let loadedBytes = 0;
    let totalBytes = 0;
    for (const v of seen.values()) {
      loadedBytes += v.loaded;
      totalBytes += v.total;
    }

    return {
      loadedBytes,
      totalBytes,
      percent: totalBytes > 0 ? Math.min(100, (loadedBytes / totalBytes) * 100) : 0,
      files: seen.size,
    };
  };
}

/**
 * The engineer's version, for Diagnostics: absolute first, percentage second,
 * file count last, because when a download misbehaves the file count is often
 * the thing that explains it.
 */
export function formatProgress(label: string, p: DownloadProgress): string {
  if (p.totalBytes === 0) return `${label}: starting`;
  return (
    `${label}: ${formatBytes(p.loadedBytes)} of ${formatBytes(p.totalBytes)}` +
    ` (${Math.round(p.percent)}%, ${p.files} file${p.files === 1 ? '' : 's'})`
  );
}

/**
 * The version for everyone else. Megabytes and a percentage are things people
 * already understand from every other download they have ever done; how many
 * files it arrived in is not, and neither is what the files are called.
 */
export function formatDownload(label: string, p: DownloadProgress): string {
  if (p.totalBytes === 0) return `${label}…`;
  return `${label}, ${Math.round(p.percent)}% (${formatBytes(p.loadedBytes)} of ${formatBytes(p.totalBytes)})`;
}
